import { getActiveModule } from "../moduleConfig";
import { offlineDb } from "./database";

const encoder = new TextEncoder();
const decoder = new TextDecoder();
const PBKDF2_ITERATIONS = 210000;
const OFFLINE_LOGIN_MAX_AGE = 30 * 24 * 60 * 60 * 1000;

const normalizeUsername = (username = "") => username.trim().toLowerCase();
const createKey = (moduleName, username) =>
  `${moduleName}::${normalizeUsername(username)}`;

const bytesToBase64 = (bytes) => {
  let binary = "";
  bytes.forEach((byte) => {
    binary += String.fromCharCode(byte);
  });
  return window.btoa(binary);
};

const base64ToBytes = (value) =>
  Uint8Array.from(window.atob(value), (character) => character.charCodeAt(0));

const deriveEncryptionKey = async (password, salt) => {
  const passwordKey = await window.crypto.subtle.importKey(
    "raw",
    encoder.encode(password),
    "PBKDF2",
    false,
    ["deriveKey"]
  );

  return window.crypto.subtle.deriveKey(
    {
      name: "PBKDF2",
      salt,
      iterations: PBKDF2_ITERATIONS,
      hash: "SHA-256",
    },
    passwordKey,
    { name: "AES-GCM", length: 256 },
    false,
    ["encrypt", "decrypt"]
  );
};

const ensureOfflineAuthSupport = () => {
  if (!window.crypto?.subtle) {
    throw new Error("La connexion hors ligne sécurisée n'est pas disponible sur cet appareil.");
  }
};

export const saveOfflineLogin = async ({
  username,
  password,
  session,
  moduleName = getActiveModule(),
}) => {
  ensureOfflineAuthSupport();
  const normalizedUsername = normalizeUsername(username);
  if (!normalizedUsername || !password || !session) return false;

  const salt = window.crypto.getRandomValues(new Uint8Array(16));
  const iv = window.crypto.getRandomValues(new Uint8Array(12));
  const encryptionKey = await deriveEncryptionKey(password, salt);
  const encryptedSession = await window.crypto.subtle.encrypt(
    { name: "AES-GCM", iv },
    encryptionKey,
    encoder.encode(JSON.stringify(session))
  );
  const updatedAt = Date.now();

  await offlineDb.offlineAuth.put({
    key: createKey(moduleName, normalizedUsername),
    module: moduleName,
    username: normalizedUsername,
    salt: bytesToBase64(salt),
    iv: bytesToBase64(iv),
    encryptedSession: bytesToBase64(new Uint8Array(encryptedSession)),
    updatedAt,
    expiresAt: updatedAt + OFFLINE_LOGIN_MAX_AGE,
  });
  return true;
};

export const restoreOfflineLogin = async ({
  username,
  password,
  moduleName = getActiveModule(),
}) => {
  ensureOfflineAuthSupport();
  const normalizedUsername = normalizeUsername(username);
  const savedLogin = await offlineDb.offlineAuth.get(
    createKey(moduleName, normalizedUsername)
  );

  if (!savedLogin) {
    throw new Error(
      "Aucune connexion hors ligne n'est enregistrée pour cet identifiant sur cet appareil."
    );
  }
  if (savedLogin.expiresAt <= Date.now()) {
    await offlineDb.offlineAuth.delete(savedLogin.key);
    throw new Error("La connexion hors ligne a expiré. Reconnectez-vous une fois à Internet.");
  }

  try {
    const encryptionKey = await deriveEncryptionKey(
      password,
      base64ToBytes(savedLogin.salt)
    );
    const decryptedSession = await window.crypto.subtle.decrypt(
      { name: "AES-GCM", iv: base64ToBytes(savedLogin.iv) },
      encryptionKey,
      base64ToBytes(savedLogin.encryptedSession)
    );
    return JSON.parse(decoder.decode(decryptedSession));
  } catch {
    throw new Error("Identifiant ou mot de passe incorrect pour la connexion hors ligne.");
  }
};

